import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, Button, TextField, MenuItem, FormControl, Typography, IconButton } from "@material-ui/core";
import CloseIcon from '@material-ui/icons/Close';
import { inputFieldStyles, commonInlineStyles } from "../styles";
import { useHistory } from "react-router-dom";
import axios from "axios";
import { appUrl } from "../../../../../../utils/config";
import { useApiBaseUrl } from "../../CommonMethods/CommonMethods";

const CMOCheckModal = ({ open, onClose, onContinue }) => {
  const [country, setCountry] = useState("");
  const [state, setState] = useState("");
  const [cmoName, setCmoName] = useState("");
  const [cmoData, setCmoData] = useState([]);
  const history = useHistory();
  const API_BASE_URL = useApiBaseUrl(appUrl)

  useEffect(() => {
    axios.post(`${appUrl}/admin/getAvailableCmos`, {ecosystemID: Number(localStorage?.ecosystemId)})
      .then(response => {
        if (response.data.status) {
          setCmoData(response.data.data);
        }
      })
      .catch(error => {
        console.error("Error fetching CMOs:", error);
      });
  }, []);

  // Unique country options
  const countryOptions = [...new Set(cmoData.map(item => item.country))];
  // Unique state options for selected country
  const stateOptions = country
    ? [...new Set(cmoData.filter(item => item.country === country).map(item => item.state))]
    : [];
  // Unique CMO name options for selected country and state
  const cmoNameOptions = country && state
    ? [...new Set(cmoData.filter(item => item.country === country && item.state === state).map(item => item.organization_name))]
    : [];

  const handleCountryChange = (event) => {
    const value = event.target.value;
    setCountry(value);
    setState(""); // Reset state when country changes
    setCmoName(""); // Reset CMO name when country changes
  };

  const handleStateChange = (event) => {
    const value = event.target.value;
    setState(value);
    setCmoName(""); // Reset CMO name when state changes
  };

  // Helper to get selected CMO's organization_id
  const getSelectedCmoOrgId = () => {
    const selected = cmoData.find(
      item => item.country === country && item.state === state && item.organization_name === cmoName
    );
    return selected ? selected.organization_id : null;
  };

  const handleContinue = async () => {
    const orgId = getSelectedCmoOrgId();
    if (!orgId) return;
    try {
      const response = await axios.post(`${API_BASE_URL}/viewCMODetails`, {
        cmoOrgId: orgId,
        ecosystemId: Number(localStorage?.ecosystemId) || 0
      });
      
      if (response.data && response.data.status) {
        // Extract the necessary data from the response
        const { cmoDetails, vendorPlantDetails } = response.data.data;
        
        // Structure the data for the CMODetails component
        const formattedData = {
          // Selected form values
          country,
          state,
          cmoName,
          organization_id: orgId,
          
          // Add readOnly flag
          readOnly: true,
          
          // Structure the data to match what CMODetails expects
          cmoDetails: {
            organizationDetails: {
              organizationName: cmoDetails.cmoName,
              orgShortName: cmoDetails.cmoShortFormName,
              streetAddress1: cmoDetails.streetAddress1,
              streetAddress2: cmoDetails.streetAddress2,
              city: cmoDetails.city,
              state: cmoDetails.state,
              country: cmoDetails.country,
              postCode: cmoDetails.postCode,
              organizationCode: cmoDetails.organizationCode,
              organizationId: cmoDetails.organizationId
            }
          },
          
          // Include vendor plant details
          vendorPlantDetails: vendorPlantDetails?.vendorPlantDetails || {},
          
          // Include IDs for reference
          vendorMosymphonyID: vendorPlantDetails?.vendorMosymphonyID,
          brandOwnerMosymphonyID: vendorPlantDetails?.brandOwnerMosymphonyID,
          
          // Include the original response for reference
          apiResponse: response.data
        };
        
        // Pass the structured data to the parent component
        onContinue(formattedData);
      } else {
        console.error("API response status is false or data is missing");
      }
    } catch (error) {
      console.error("Error fetching CMO Org Details:", error);
      // Optionally, handle error UI here
    }
  };

  return (
    <Dialog className="cmo-check-modal" open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle style={{ textAlign: 'center', fontWeight: 600, fontSize: 24 }}>
        Please check if CMO is already in the list
        <IconButton aria-label="close" onClick={onClose} style={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <div style={{ display: 'flex', gap: 30, marginBottom: 30 }}>
          <FormControl fullWidth>
            <Typography component="label" htmlFor="country">
              Select Country <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              select
              id="country"
              value={country}
              onChange={handleCountryChange}
              variant="outlined"
              fullWidth
              required
              InputProps={{ style: commonInlineStyles.input }}
              sx={inputFieldStyles}
              inputProps={commonInlineStyles.inputProps}
              style={commonInlineStyles.input}
            >
              {countryOptions.map(c => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </TextField>
          </FormControl>
          <FormControl fullWidth>
            <Typography component="label" htmlFor="state">
              Select State <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              select
              id="state"
              value={state}
              onChange={handleStateChange}
              variant="outlined"
              fullWidth
              required
              disabled={!country}
              InputProps={{ style: commonInlineStyles.input }}
              sx={inputFieldStyles}
              inputProps={commonInlineStyles.inputProps}
              style={commonInlineStyles.input}
            >
              {stateOptions.map(s => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </TextField>
          </FormControl>
        </div>
        <FormControl fullWidth>
          <Typography component="label" htmlFor="cmoName">
            Select CMO Name <span style={{ color: "red" }}>*</span>
          </Typography>
          <TextField
            select
            id="cmoName"
            value={cmoName}
            onChange={e => setCmoName(e.target.value)}
            variant="outlined"
            fullWidth
            required
            disabled={!country || !state}
            InputProps={{ style: commonInlineStyles.input }}
            sx={inputFieldStyles}
            inputProps={commonInlineStyles.inputProps}
            style={commonInlineStyles.input}
          >
            {cmoNameOptions.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
          </TextField>
        </FormControl>
        <div className="cmo-check-modal-button" style={{ marginTop: 32, textAlign: "center" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleContinue}
            disabled={!country || !state || !cmoName}
            style={{ minWidth: 160 }}
          >
            Continue
          </Button>
        </div>
        <div style={{ marginTop: 24, textAlign: "center", fontSize: 14 }}>
          <Typography className="suggestion-text">Country/state/sponsor name not in the list?</Typography>
          <button className="add-new-details-button"  onClick={e => { e.preventDefault(); history.push('addCMO'); }}>
            Click here to add new details
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CMOCheckModal; 